import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Col, Statistic, Typography, ConfigProvider, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchMerchants, deleteMerchant } from '../../redux/MerchantsUser';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { ThemeProvider } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import MerchantBox from './MerchantBox';
import MerchantsTable from './MerchantsTable';
import CustomInput from '../../commonComponents/input';
import { Merchant } from './types';
import {
  Container,
  StyledCard,
  StatsRow,
  StatCard,
  HeaderSection,
  TopControlBar,
  AddButton,
} from './styles';

const { Title } = Typography;

const Merchants: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isEditing, setIsEditing] = useState(false);
  const [currentMerchant, setCurrentMerchant] = useState<Merchant | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const dispatch = useDispatch<AppDispatch>();
  const { merchants, loading } = useSelector((state: RootState) => state.merchants);
  const hasFetchedRef = useRef(false);

  const filteredMerchants = useMemo(() => {
    if (!Array.isArray(merchants)) return [];
    if (!searchQuery) return merchants;
    const query = searchQuery.toLowerCase();
    return merchants.filter(
      (merchant) =>
        merchant?.name?.toLowerCase().includes(query) ||
        merchant?.username?.toLowerCase().includes(query) ||
        merchant?.email?.toLowerCase().includes(query)
    );
  }, [merchants, searchQuery]);

  const paginatedMerchants = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredMerchants.slice(startIndex, endIndex);
  }, [filteredMerchants, currentPage, pageSize]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchMerchants());
    }
  }, [dispatch]);

  const handleAddClick = () => {
    setCurrentMerchant(null);
    setIsEditing(true);
  };

  const handleEdit = (merchant: Merchant) => {
    setCurrentMerchant(merchant);
    setIsEditing(true);
  };

  const handleDelete = async (merchant: Merchant) => {
    const result = await dispatch(deleteMerchant({ type: 'merchant', id: merchant.id }));
    if (deleteMerchant.fulfilled.match(result)) {
      message.success('Merchant deleted successfully');
      dispatch(fetchMerchants());
    } else {
      const errorMsg = typeof result.payload === 'string' ? result.payload : 'Error deleting merchant';
      message.error(errorMsg);
    }
  };

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          {!isEditing ? (
            <>
              <StatsRow gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <StatCard>
                    <Statistic
                      title="Total Merchants"
                      value={merchants.length}
                      valueStyle={{ color: '#1890ff' }}
                      prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                    />
                  </StatCard>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <StatCard>
                    <Statistic
                      title="Filtered Results"
                      value={filteredMerchants.length}
                      valueStyle={{ color: '#52c41a' }}
                      prefix={<UserOutlined style={{ color: '#52c41a' }} />}
                    />
                  </StatCard>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <StatCard
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/admin/merchant-earnings')}
                  >
                    <Statistic
                      title="Merchant Earnings"
                      value="View Overview"
                      valueStyle={{ color: '#fa8c16', fontSize: '18px' }}
                      prefix={<UserOutlined style={{ color: '#fa8c16' }} />}
                    />
                  </StatCard>
                </Col>
              </StatsRow>

              <StyledCard>
                <HeaderSection>
                  <div>
                    <Title level={2} style={{ margin: 0 }}>
                      Merchants
                    </Title>
                    <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                      Manage your merchant accounts and partnerships
                    </Typography.Text>
                  </div>
                  <AddButton
                    type="primary"
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={handleAddClick}
                    size="large"
                  >
                    Add Merchant
                  </AddButton>
                </HeaderSection>

                <TopControlBar>
                  <CustomInput
                    placeholder="Search merchants by name, username, or email"
                    value={searchQuery}
                    onChange={(value: string) => setSearchQuery(value)}
                    allowClear
                    width="300px"
                  />
                </TopControlBar>

                <MerchantsTable
                  merchants={paginatedMerchants}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  total={filteredMerchants.length}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  loading={loading.merchants}
                />
              </StyledCard>
            </>
          ) : (
            <StyledCard>
              <MerchantBox
                initialMerchant={currentMerchant}
                onSave={() => {
                  setIsEditing(false);
                  setCurrentMerchant(null);
                  dispatch(fetchMerchants());
                }}
                onCancel={() => {
                  setIsEditing(false);
                  setCurrentMerchant(null);
                }}
                fetchMerchants={() => dispatch(fetchMerchants())}
              />
            </StyledCard>
          )}
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default Merchants;